import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { useFlagsmith, useFlagsmithLoading } from 'flagsmith/react';

import { sanitizeTagValue } from '~/utils/instrumentation';
import { createLogger } from '~/utils/logging';
import { Loading } from '~/components/Loading';
import { type UserProfile } from '~/services/types';
import { useUserProfile } from '~/services/useUserProfileQuery';

type FlagsmithIdentityContextValue = {
  isLoading: boolean;
};

type FlagsmithTraits = Partial<Pick<UserProfile, 'email' | 'name' | 'role' | 'schoolId'>>;

type IdentityState = {
  prevEmail?: string;
  fetchedAnonymous: boolean;
  identifying: boolean;
};

export const FlagsmithIdentityContext = createContext<FlagsmithIdentityContextValue>({ isLoading: true });

const log = createLogger('FlagsmithIdentityProvider');

export function FlagsmithIdentityProvider({ children }: PropsWithChildren) {
  const { user, isLoading: isUserProfileLoading } = useUserProfile();
  const flagsmith = useFlagsmith();
  const flagsmithSource = useFlagsmithLoading()?.source;

  const traits = useMemo<FlagsmithTraits>(() => {
    if (!user) return {};
    const { email, name, role, schoolId } = user;
    return { email, name, role, schoolId };
  }, [user]);

  const [identity, setIdentity] = useState<IdentityState>({
    prevEmail: undefined,
    fetchedAnonymous: false,
    identifying: false,
  });

  const fetchAnonymousFlags = useCallback(async () => {
    if (identity.fetchedAnonymous) return;
    try {
      await flagsmith.getFlags();
    } catch (err) {
      log.error('Flagsmith getFlags (anonymous) failed', {
        errorMessage: sanitizeTagValue(err instanceof Error ? err.message : 'Unknown error'),
      });
    }
    setIdentity((s) => ({ ...s, fetchedAnonymous: true }));
  }, [identity.fetchedAnonymous, flagsmith]);

  const logout = useCallback(async () => {
    try {
      // flagsmith.logout() will re-fetch the default (anonymous) flags.
      await flagsmith.logout();
    } catch (err) {
      log.error('Flagsmith logout failed', {
        errorMessage: sanitizeTagValue(err instanceof Error ? err.message : 'Unknown error'),
      });
    }
    setIdentity({ prevEmail: undefined, fetchedAnonymous: true, identifying: false });
  }, [flagsmith]);

  const login = useCallback(
    async (email: string) => {
      setIdentity((s) => ({ ...s, identifying: true }));

      let identifySucceeded = false;
      try {
        await flagsmith.identify(email, traits);
        identifySucceeded = true;
      } catch (err) {
        log.error('Flagsmith identify failed', {
          errorMessage: sanitizeTagValue(err instanceof Error ? err.message : 'Unknown error'),
        });
      } finally {
        setIdentity((s) => ({
          prevEmail: identifySucceeded ? email : s.prevEmail,
          fetchedAnonymous: false,
          identifying: false,
        }));
      }
    },
    [flagsmith, traits],
  );

  useEffect(() => {
    if (isUserProfileLoading) return;

    const email = traits.email;

    if (!email && identity.prevEmail === undefined) {
      fetchAnonymousFlags();
      return;
    }

    if (!email && identity.prevEmail !== undefined) {
      logout();
      return;
    }

    if (email && identity.prevEmail !== email) {
      login(email);
    }
  }, [isUserProfileLoading, traits, identity.prevEmail, fetchAnonymousFlags, login, logout]);

  const isLoading = flagsmithSource !== 'SERVER' || identity.identifying;

  return (
    <FlagsmithIdentityContext.Provider value={{ isLoading }}>
      {isLoading ? <Loading /> : children}
    </FlagsmithIdentityContext.Provider>
  );
}
